import {
  useCreateUnderwearPromotion,
  useUpdateUnderwearPromotion,
} from '@/api/underwear-promotion/underwear-promotion.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useZodForm,
} from '@/lib/form';
import {
  underwearPromotionSchema,
  updateUnderwearPromotionSchema,
  UpdateUnderwearPromotionSchemaType,
} from '@/schemas/underwear-promotion.schema';
import { UnderwearPromotionResponse } from '@repo/types/src/underwear-promotion';
import { CirclePlus, Pencil } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  promotion?: UnderwearPromotionResponse;
}

export const AddUnderwearPromotion = ({ promotion }: Props = {}) => {
  const isEdit = promotion !== undefined;
  const createMutation = useCreateUnderwearPromotion();
  const updateMutation = useUpdateUnderwearPromotion();
  const [open, setOpen] = useState(false);

  const form = useZodForm({
    schema: isEdit ? updateUnderwearPromotionSchema : underwearPromotionSchema,
    defaultValues: {
      name: promotion?.name ?? '',
      description: promotion?.description ?? '',
      link: promotion?.link ?? '',
      timeStart: promotion?.timeStart ?? 0,
      occurrence: promotion?.occurrence ?? 1,
      nthOpen: promotion?.nthOpen ?? 1,
    },
  });

  const handleSubmit = (payload: UpdateUnderwearPromotionSchemaType) => {
    if (isEdit) {
      updateMutation.mutate(
        { id: promotion.id, payload },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Promotion modifiée avec succès !', {
              duration: 3000,
            });
          },
          onError: () =>
            toast.error('Erreur lors de la modification', { duration: 3000 }),
        },
      );
    } else {
      if (!payload.file) {
        toast.error('Veuillez ajouter une image', { duration: 3000 });
        return;
      }
      createMutation.mutate(
        { ...payload, file: payload.file },
        {
          onSuccess: () => {
            setOpen(false);
            toast.success('Promotion créée avec succès !', { duration: 3000 });
          },
          onError: () =>
            toast.error('Erreur lors de la création', { duration: 3000 }),
        },
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <Button variant="ghost" size="icon">
            <Pencil size={20} />
          </Button>
        ) : (
          <Button>
            <div className="flex flex-row gap-2 items-center">
              <CirclePlus size={24} />
              <span>Ajouter une promotion</span>
            </div>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <Form
          form={form}
          onSubmit={handleSubmit}
          className="w-full flex flex-col space-y-4"
        >
          <DialogHeader />
          <DialogTitle className="text-center">
            {isEdit ? 'Modifier' : 'Ajouter'} une promotion
          </DialogTitle>
          <DialogDescription className="text-center">
            {isEdit
              ? 'Modifier la promotion culotte'
              : 'Ajouter une promotion culotte'}
          </DialogDescription>

          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nom</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="Ex: Promo été" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="Ex: -20% sur toutes les culottes"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lien</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="https://..." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="timeStart"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Délai d'affichage (secondes)</FormLabel>
                <FormControl>
                  <Input type="number" min={0} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="occurrence"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Occurrence</FormLabel>
                <FormControl>
                  <Input type="number" min={1} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="nthOpen"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nième ouverture</FormLabel>
                <FormControl>
                  <Input type="number" min={1} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="file"
            render={({ field: { value: _, onChange, ...fieldProps } }) => (
              <FormItem>
                <FormLabel>Image</FormLabel>
                {isEdit && (
                  <FormDescription>
                    Laisser vide pour conserver l'image actuelle
                  </FormDescription>
                )}
                <FormControl>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    {...fieldProps}
                    onChange={(e) => onChange(e.target.files?.[0])}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="flex gap-2">
            <DialogClose asChild>
              <Button variant="ghost">Annuler</Button>
            </DialogClose>
            <Button type="submit">{isEdit ? 'Modifier' : 'Ajouter'}</Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
